import ContentOverlay from '@/components/ContentOverlay';
import CurrencySelectInput from '@/components/CurrencySelectInput';
import { useAuth } from '@/config/AuthContext/useAuth';
import { getCurrency } from '@/features/invoices/util';
import { useToastApi } from '@/hooks/useToastApi';
import { SUBMIT_STATUSES, SWITCH_LABELS } from '@inspection/constants';
import { completeInspection } from '@inspection/services/inspections.service';
import { TAjaxInspectionCompletionForm, TCompleteInspection, TSubmittedDraft } from '@inspection/types';
import { useMutation } from '@tanstack/react-query';
import { Button, Col, Divider, Flex, Form, Row, Space, Statistic, Switch, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { CSSProperties, ReactNode, useEffect, useState } from 'react';

type Props = {
  uniqueCode: string;
  children: ReactNode;
  sumAssured: number;
  submittedDraft: TSubmittedDraft;
  refetch: () => void;
  disable?: boolean;
};

const AjaxCompleteInspection = ({
  uniqueCode,
  sumAssured,
  children,
  submittedDraft,
  refetch,
  disable = false,
}: Props) => {
  const styles = useStyles();
  const [form] = Form.useForm();
  const toastApi = useToastApi();
  const {
    authState: { tenantId, token },
  } = useAuth();
  const { Text } = Typography;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const CURRENCY = getCurrency(tenantId);
  const [showCompleteInspectionOverlay, setShowCompleteInspectionOverlay] = useState(false);

  const onCancel = () => {
    form.resetFields();
    setShowCompleteInspectionOverlay(false);
  };

  useEffect(() => {
    if (submittedDraft?.isCustomerDecisionPending) {
      form.setFieldsValue({
        submitStatus: SUBMIT_STATUSES.CUSTOMER_DECISION_PENDING,
        valuation: parseFloat(submittedDraft.valuation),
        offerStatus: submittedDraft.offerStatus,
        consistency: submittedDraft.consistency,
        exceedReport: submittedDraft.exceedReport,
        directSettlement: submittedDraft.directSettlement,
        operatorComment: submittedDraft.operatorComment,
        mandateGiven: parseFloat(submittedDraft.mandateGiven ?? '0'),
        offerAmount: parseFloat(submittedDraft.offerAmount),
      });
    }
  }, [submittedDraft, form, showCompleteInspectionOverlay]);

  const mutation = useMutation({
    mutationFn: ({ tenantId, token, uniqueCode, form: submittedValues }: TCompleteInspection) =>
      completeInspection({ tenantId, token, uniqueCode, form: submittedValues }),
    onSuccess: (data) => {
      form.resetFields();
      setShowCompleteInspectionOverlay(false);
      setIsSubmitted(false);
      toastApi.open({
        type: data?.success ? 'success' : 'error',
        content: data?.success ? 'Inspection completed successfully!' : data.message,
        duration: 2,
      });
      data?.success && refetch();
    },
    onError: (data) => {
      form.resetFields();
      setShowCompleteInspectionOverlay(false);
      toastApi.open({
        type: 'error',
        content: data?.message || 'Something went wrong!',
        duration: 2,
      });
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinish = (values: TAjaxInspectionCompletionForm) => {
    const submittedValues = {
      uniqueCode: uniqueCode,
      submitStatus: 'completed',
      valuation: values.valuation.toString(),
      offerStatus: values.offerStatus ? 'true' : 'false',
      consistency: values.consistency ? 'true' : 'false',
      exceedReport: values.exceedReport ? 'true' : 'false',
      directSettlement: values.directSettlement ? 'true' : 'false',
      operatorComment: values.operatorComment,
      mandateGiven: values.mandateGiven.toString(),
      offerAmount: values.offerAmount ? values.offerAmount.toString() : null,
    };
    mutation.mutate({ tenantId, token, uniqueCode, form: submittedValues });
  };

  return (
    <>
      <ContentOverlay
        title={isSubmitted ? 'Confirm Submission' : 'Complete Inspection'}
        open={showCompleteInspectionOverlay}
        onCancel={() => setShowCompleteInspectionOverlay(false)}
        width="700px"
        afterClose={onCancel}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginRight: '16px' }}>
          <div style={{ display: isSubmitted ? 'none' : '' }}>
            <Divider style={styles.horizontalDividerTop} />

            <Flex justify="space-between" gap={8}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.submitStatus !== currentValues.submitStatus}
              >
                {({ getFieldValue, setFieldValue }) => (
                  <Form.Item
                    label={'Offer Status'}
                    name={'offerStatus'}
                    style={{ margin: '2px' }}
                    required={getFieldValue('submitStatus') !== SUBMIT_STATUSES.CUSTOMER_DECISION_PENDING}
                  >
                    <Switch
                      checkedChildren={SWITCH_LABELS.YES}
                      unCheckedChildren={
                        getFieldValue('submitStatus') === SUBMIT_STATUSES.CUSTOMER_DECISION_PENDING
                          ? SWITCH_LABELS.CDP
                          : SWITCH_LABELS.NO
                      }
                      disabled={getFieldValue('submitStatus') === SUBMIT_STATUSES.CUSTOMER_DECISION_PENDING}
                      onChange={(value) => {
                        if (!value) {
                          setFieldValue('offerAmount', undefined);
                          setFieldValue('surveyorOffer', undefined);
                        }
                      }}
                    />
                  </Form.Item>
                )}
              </Form.Item>

              <Divider type="vertical" style={styles.verticalDivider} />

              <Form.Item label={'Consistency'} name={'consistency'} style={{ margin: '2px' }} required>
                <Switch checkedChildren={SWITCH_LABELS.YES} unCheckedChildren={SWITCH_LABELS.NO} />
              </Form.Item>

              <Divider type="vertical" style={styles.verticalDivider} />

              <Form.Item label={'Exceed Report'} name={'exceedReport'} style={{ margin: '2px' }} required>
                <Switch checkedChildren={SWITCH_LABELS.YES} unCheckedChildren={SWITCH_LABELS.NO} />
              </Form.Item>

              <Divider type="vertical" style={styles.verticalDivider} />

              <Form.Item label={'Direct Settlement'} name={'directSettlement'} style={{ margin: '2px' }} required>
                <Switch checkedChildren={SWITCH_LABELS.YES} unCheckedChildren={SWITCH_LABELS.NO} defaultValue={false} />
              </Form.Item>
            </Flex>

            <Divider style={styles.horizontalDividerBottom} />

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label={'Repairer Estimate'}
                  name={'valuation'}
                  rules={
                    sumAssured === 0
                      ? [{ required: true, min: 0, type: 'number' }]
                      : [
                          {
                            required: true,
                          },
                          {
                            min: 0,
                            type: 'number',
                            max: sumAssured,
                            message: 'Repairer Estimate cannot exceed sum assured',
                          },
                        ]
                  }
                >
                  <CurrencySelectInput style={styles.fullWidth} defaultCurrency={CURRENCY} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  label={'Mandate Given'}
                  name={'mandateGiven'}
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <CurrencySelectInput style={styles.fullWidth} defaultCurrency={CURRENCY} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.offerStatus !== currentValues.offerStatus ||
                    prevValues.valuation !== currentValues.valuation ||
                    prevValues.submitStatus !== currentValues.submitStatus
                  }
                >
                  {({ getFieldValue }) => (
                    <Form.Item
                      label={'Surveyor Offer'}
                      name={'offerAmount'}
                      rules={[
                        {
                          required:
                            getFieldValue('offerStatus') ||
                            getFieldValue('submitStatus') === SUBMIT_STATUSES.CUSTOMER_DECISION_PENDING,
                        },
                        {
                          min: 0,
                          type: 'number',
                          max: getFieldValue('valuation'),
                          message: 'Offer amount cannot exceed Repairer Estimate',
                        },
                      ]}
                    >
                      <CurrencySelectInput
                        defaultCurrency={CURRENCY}
                        min={0}
                        style={styles.fullWidth}
                        disabled={
                          !getFieldValue('offerStatus') &&
                          getFieldValue('submitStatus') !== SUBMIT_STATUSES.CUSTOMER_DECISION_PENDING
                        }
                        placeholder={
                          !getFieldValue('offerStatus') &&
                          getFieldValue('submitStatus') !== SUBMIT_STATUSES.CUSTOMER_DECISION_PENDING
                            ? 'N/A'
                            : ''
                        }
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label={'Comments'} name={'operatorComment'} rules={[{ required: true }]}>
              <TextArea rows={2} />
            </Form.Item>

            <Flex justify="end" gap={8}>
              <Form.Item style={{ marginBottom: '4px' }}>
                <Button type="default" onClick={onCancel}>
                  Cancel
                </Button>
              </Form.Item>

              <Form.Item style={{ marginBottom: '4px' }}>
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={() => {
                    form
                      .validateFields()
                      .then(() => {
                        setIsSubmitted(true);
                      })
                      .catch(() => {
                        setIsSubmitted(false);
                      });
                  }}
                  loading={mutation.isPending}
                >
                  Submit
                </Button>
              </Form.Item>
            </Flex>
          </div>
          <div style={{ display: isSubmitted ? '' : 'none' }}>
            <Text type="danger">Please ensure the entered amounts are accurate!</Text>
            <Flex justify={'space-evenly'} style={styles.statisticsContainer}>
              <Statistic
                valueStyle={styles.statistic(form.getFieldValue('valuation'))}
                title={<Text>Repairer Estimate</Text>}
                prefix={`${CURRENCY}`}
                value={form.getFieldValue('valuation') || 'N/A'}
              />
              <>
                <Divider type="vertical" style={styles.verticalDivider} />
                <Statistic
                  valueStyle={styles.statistic(form.getFieldValue('mandateGiven'))}
                  title={<Text>Mandate given</Text>}
                  prefix={form.getFieldValue('mandateGiven') && `${CURRENCY}`}
                  value={form.getFieldValue('mandateGiven') || 'N/A'}
                />
              </>
              <></>
              <>
                <Divider type="vertical" style={styles.verticalDivider} />
                <Statistic
                  valueStyle={styles.statistic(form.getFieldValue('offerAmount'))}
                  title={<Text>Surveyor Offer</Text>}
                  prefix={form.getFieldValue('offerAmount') && `${CURRENCY}`}
                  value={form.getFieldValue('offerAmount') || 'N/A'}
                />
              </>
            </Flex>
            <Flex justify="end" gap={8}>
              <Form.Item style={{ marginBottom: '4px' }}>
                <Button type="default" onClick={() => setIsSubmitted(false)}>
                  Back
                </Button>
              </Form.Item>

              <Form.Item style={{ marginBottom: '4px' }}>
                <Button type="primary" htmlType="submit" loading={mutation.isPending}>
                  Confirm
                </Button>
              </Form.Item>
            </Flex>
          </div>
        </Form>
      </ContentOverlay>

      <Space
        {...(!disable && { onClick: () => setShowCompleteInspectionOverlay(!showCompleteInspectionOverlay) })}
        style={styles.childSpace(disable)}
      >
        {children}
      </Space>
    </>
  );
};

export default AjaxCompleteInspection;

const useStyles = () => {
  return {
    verticalDivider: {
      backgroundColor: '#d9d9d9',
      height: '60px',
      marginTop: 'auto',
      marginBottom: 'auto',
    } as CSSProperties,
    horizontalDividerTop: {
      backgroundColor: '#d9d9d9',
      margin: '16px 0 8px 0',
    } as CSSProperties,
    horizontalDividerBottom: {
      backgroundColor: '#d9d9d9',
      margin: '8px 0 24px 0',
    } as CSSProperties,
    fullWidth: {
      width: '100%',
    } as CSSProperties,
    statisticsContainer: {
      margin: '32px 0',
    } as CSSProperties,
    statistic: (value?: boolean): CSSProperties => ({
      color: value ? 'black' : 'grey',
      fontSize: 24,
    }),
    childSpace: (disabled: boolean): CSSProperties => ({
      color: disabled ? 'rgba(0, 0, 0, 0.25)' : 'black',
      cursor: disabled ? 'not-allowed' : 'pointer',
    }),
  };
};
